import { useState } from "react";
import {
    Box,
    Button,
    Paper,
    Table,
    TableBody,
    TableCell,
    TableHead,
    TablePagination,
    TableRow,
    Typography,
} from "@mui/material";

import { exportTransportHistoryPdf } from "./pdfExport";
import type { TransportHistoryRow } from "./types";
import { formatDate } from "./utils";

interface TransportHistoryCardProps {
    embedded?: boolean;
    rows: TransportHistoryRow[];
    source?: "transport-db" | "orders" | "none";
}

export default function TransportHistoryCard({
    embedded = false,
    rows,
    source = "none",
}: TransportHistoryCardProps) {
    const [page, setPage] = useState(0);
    const [rowsPerPage, setRowsPerPage] = useState(10);

    const paginatedRows = rows.slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage);

    return (
        <Paper className="table-card" elevation={0}>
            <Box className="table-card__header" sx={{ display: "flex", gap: 1.5, justifyContent: "space-between", alignItems: { sm: "center" }, flexDirection: { xs: "column", sm: "row" } }}>
                <Box>
                    {!embedded ? <Typography variant="h5">Транспортна история</Typography> : null}
                    {source === "orders" ? (
                        <Typography variant="body2" color="text.secondary">
                            Данни от шофьор/бус/маршрут по документите (Transport DB не върна редове).
                        </Typography>
                    ) : embedded ? (
                        <Typography variant="body2" color="text.secondary">
                            Доставки и маршрути от Transport DB за избрания клиент.
                        </Typography>
                    ) : null}
                </Box>
                <Button
                    variant="outlined"
                    size="small"
                    disabled={rows.length === 0}
                    onClick={() => exportTransportHistoryPdf(rows)}
                >
                    PDF таблица
                </Button>
            </Box>
            <Box className="table-card__scroller">
                <Table size="small">
                    <TableHead>
                        <TableRow>
                            <TableCell>Дата</TableCell>
                            <TableCell>Клиент</TableCell>
                            <TableCell>Локация</TableCell>
                            <TableCell>Бус</TableCell>
                            <TableCell>Маршрут</TableCell>
                            <TableCell>Бележка</TableCell>
                            <TableCell>Код</TableCell>
                        </TableRow>
                    </TableHead>
                    <TableBody>
                        {rows.length === 0 ? (
                            <TableRow>
                                <TableCell colSpan={7}>
                                    <Typography color="text.secondary">
                                        Няма записи в Transport DB за този клиент. Ако има доставки, те трябва да
                                        се виждат в колоната „Шофьор / Бус“ в историята по документи.
                                    </Typography>
                                </TableCell>
                            </TableRow>
                        ) : (
                            paginatedRows.map((row) => (
                                <TableRow key={`${row.id}-${row.transportDate}`} hover>
                                    <TableCell>{formatDate(row.transportDate)}</TableCell>
                                    <TableCell>{row.clientName || "—"}</TableCell>
                                    <TableCell>{row.location || "—"}</TableCell>
                                    <TableCell>{row.bus || "—"}</TableCell>
                                    <TableCell>{row.route || "—"}</TableCell>
                                    <TableCell>{row.note || "—"}</TableCell>
                                    <TableCell>{row.clientCode || "—"}</TableCell>
                                </TableRow>
                            ))
                        )}
                    </TableBody>
                </Table>
            </Box>
            <TablePagination
                component="div"
                count={rows.length}
                page={page}
                onPageChange={(_, nextPage) => setPage(nextPage)}
                rowsPerPage={rowsPerPage}
                onRowsPerPageChange={(event) => {
                    setRowsPerPage(Number(event.target.value));
                    setPage(0);
                }}
                rowsPerPageOptions={[5, 10, 25, 50]}
                labelRowsPerPage="Редове"
            />
        </Paper>
    );
}
